import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  MagnifyingGlassIcon, 
  UserIcon, 
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { fetchApi } from '@/utils/fetchApi';
import { appointmentsService, type Appointment } from '../../services/appointmentsService';

// TypeScript interfaces
interface Patient {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
}

interface PatientAppointmentSearchProps {
  onPatientSelect?: (patientId: string, appointments: Appointment[], patient: Patient | null) => void;
  onAppointmentNavigate?: (appointment: Appointment) => void;
  onClose?: () => void;
  className?: string;
  selectedPatientAppointments?: Appointment[];
  currentPatientAppointmentIndex?: number;
  onClearFilter?: () => void;
  initialPatient?: { id: string; firstName: string; lastName: string } | null;
}

export default function PatientAppointmentSearch({ 
  onPatientSelect, 
  onAppointmentNavigate, 
  onClose: _onClose,
  className = '',
  selectedPatientAppointments = [],
  currentPatientAppointmentIndex = 0,
  onClearFilter,
  initialPatient = null
}: PatientAppointmentSearchProps) {
  const { t } = useTranslation(['appointments', 'common']);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [filteredPatients, setFilteredPatients] = useState<Patient[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [patientAppointments, setPatientAppointments] = useState<Appointment[]>([]);
  const [currentAppointmentIndex, setCurrentAppointmentIndex] = useState(0);
  const [isLoadingPatients, setIsLoadingPatients] = useState(false);
  const [isLoadingAppointments, setIsLoadingAppointments] = useState(false);
  const [isCardExpanded, setIsCardExpanded] = useState(false);
  
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load patients on component mount
  useEffect(() => {
    loadPatients();
  }, []);

  // Auto-select patient when navigated from Patients page
  useEffect(() => {
    if (initialPatient && patients.length > 0 && !selectedPatient) {
      const matchedPatient = patients.find(p => p.id === initialPatient.id);
      if (matchedPatient) {
        handlePatientSelect(matchedPatient);
      } else {
        // If patient not found by ID, use the data passed from navigation
        handlePatientSelect({
          id: initialPatient.id,
          firstName: initialPatient.firstName,
          lastName: initialPatient.lastName,
        });
      }
    }
  }, [initialPatient, patients]);

  // Filter patients based on search query
  useEffect(() => {
    if (!searchQuery.trim() || selectedPatient) {
      setFilteredPatients([]);
      setShowDropdown(false);
      return;
    }

    const query = searchQuery.toLowerCase().trim();
    const filtered = patients.filter(patient => {
      const fullName = `${patient.firstName || ''} ${patient.lastName || ''}`.toLowerCase();
      const email = (patient.email || '').toLowerCase();
      const phone = (patient.phone || '').toLowerCase();
      
      return fullName.includes(query) || 
             email.includes(query) || 
             phone.includes(query);
    });

    setFilteredPatients(filtered);
    setShowDropdown(filtered.length > 0 && !selectedPatient);
  }, [searchQuery, patients, selectedPatient]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadPatients = async () => {
    setIsLoadingPatients(true);
    try {
      console.log('Loading patients from: patients/api/list/');
      const response = await fetchApi('patients/api/list/');
      console.log('Patients response:', response);
      
      if (response.success) {
        setPatients(response.patients || []);
      } else {
        console.error('Failed to load patients:', response.message);
        setPatients([]);
      }
    } catch (error) {
      console.error('Error loading patients:', error);
      setPatients([]);
    } finally {
      setIsLoadingPatients(false);
    }
  };

  const loadPatientAppointments = async (patientId: string, patient: Patient | null = null) => {
    setIsLoadingAppointments(true);
    // Use passed patient or fall back to selectedPatient state
    const patientToUse = patient || selectedPatient;
    
    try {
      console.log('🔍 Loading appointments for patient ID:', patientId);
      const appointments = await appointmentsService.getAppointmentsByPatient(patientId);
      console.log('📋 Raw appointments response:', appointments);
      
      if (!appointments || appointments.length === 0) {
        console.log('⚠️ No appointments found for patient:', patientId);
        setPatientAppointments([]);
        if (onPatientSelect) {
          onPatientSelect(patientId, [], patientToUse);
        }
        return;
      }
      
      // Sort appointments by date (most recent first)
      const sortedAppointments = appointments.sort((a, b) => {
        const dateA = new Date(`${a.appointment_date} ${a.appointment_time}`);
        const dateB = new Date(`${b.appointment_date} ${b.appointment_time}`);
        return dateB.getTime() - dateA.getTime();
      });
      
      console.log('📅 Sorted appointments:', sortedAppointments);
      setPatientAppointments(sortedAppointments);
      setCurrentAppointmentIndex(0);
      
      // Notify parent component
      if (onPatientSelect) {
        onPatientSelect(patientId, sortedAppointments, patientToUse);
      }
      
    } catch (error) {
      console.error('❌ Error loading patient appointments:', error);
      setPatientAppointments([]);
      if (onPatientSelect) {
        onPatientSelect(patientId, [], patientToUse);
      }
    } finally {
      setIsLoadingAppointments(false);
    }
  };

  const handlePatientSelect = async (patient: Patient) => {
    console.log('🔍 Patient selected:', patient);
    setSelectedPatient(patient);
    setSearchQuery(`${patient.firstName || ''} ${patient.lastName || ''}`.trim());  
    setShowDropdown(false);
    
    // Load appointments for selected patient - pass patient object to avoid state timing issues
    await loadPatientAppointments(patient.id, patient);
  };

  const navigateToAppointment = (index: number) => {
    if (index >= 0 && index < patientAppointments.length) {
      setCurrentAppointmentIndex(index);
      const appointment = patientAppointments[index];
      
      // Notify parent component to navigate to this appointment
      if (onAppointmentNavigate) {
        onAppointmentNavigate(appointment);
      }
    }
  };

  // Enhanced navigation that also updates the calendar date
  const navigateToAppointmentWithCalendar = (index: number) => {
    navigateToAppointment(index);
    
    // Also notify parent to update calendar view
    if (index >= 0 && index < patientAppointments.length) {
      const appointment = patientAppointments[index];
      if (onAppointmentNavigate) {
        onAppointmentNavigate(appointment);
      }
    }
  };

  const handlePreviousAppointment = () => {
    const newIndex = Math.min(currentAppointmentIndex + 1, patientAppointments.length - 1);
    navigateToAppointmentWithCalendar(newIndex);
  };

  const handleNextAppointment = () => {
    const newIndex = Math.max(currentAppointmentIndex - 1, 0);
    navigateToAppointmentWithCalendar(newIndex);
  };

  const handleClear = () => {
    setSearchQuery('');
    setSelectedPatient(null);
    setPatientAppointments([]);
    setCurrentAppointmentIndex(0);
    setShowDropdown(false);
    setIsCardExpanded(false); // Reset expanded state
    
    // Notify parent to clear the patient filter
    if (onPatientSelect) {
      onPatientSelect('', [], null);
    }
    
    // Additional callback for clearing filter
    if (onClearFilter) {
      onClearFilter();
    }
  };

  const formatDate = (dateStr: string, timeStr: string) => {
    try {
      const date = new Date(`${dateStr} ${timeStr}`);
      return {
        date: date.toLocaleDateString('en-US', { 
          weekday: 'short', 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric' 
        }),
        time: date.toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit', 
          hour12: true 
        })
      };
    } catch {
      return { date: dateStr, time: timeStr };
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'confirmed': return 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700/40';
      case 'scheduled': return 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700/40';
      case 'completed': return 'text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/40 border-gray-200 dark:border-gray-700/40';
      case 'cancelled': return 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700/40';
      case 'in progress': return 'text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-700/40';
      case 'no show': return 'text-red-500 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700/40';
      default: return 'text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/40 border-gray-200 dark:border-gray-700/40';
    }
  };

  const currentAppointment = patientAppointments[currentAppointmentIndex];

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Main Search Input */}
      <div className="relative">
        <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
        <input
          ref={searchInputRef}
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => {
            if (!selectedPatient && searchQuery) {
              setShowDropdown(filteredPatients.length > 0);
            }
          }}
          placeholder={selectedPatient ? `Filtering by ${selectedPatient.firstName} ${selectedPatient.lastName}` : "Search appointments..."}
          className={`w-full pl-10 pr-10 py-2 rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm transition-colors ${
            selectedPatient 
              ? 'bg-background border-primary/30 border-2' 
              : 'bg-background border border-border focus:border-primary'
          }`}
          disabled={isLoadingPatients}
        />
        {searchQuery && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        )}

        {/* Loading indicator */}
        {isLoadingPatients && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
          </div>
        )}
      </div>

      {/* Compact Patient Card - Click to expand */}
      {selectedPatientAppointments.length > 0 && selectedPatient && !isCardExpanded && (
        <div 
          className="mt-2 bg-primary/5 border border-primary/20 rounded-lg p-3 cursor-pointer hover:bg-primary/10 transition-colors"
          onClick={() => setIsCardExpanded(true)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                <UserIcon className="w-4 h-4 text-primary" />
              </div>
              <div>
                <div className="font-medium text-foreground text-sm">
                  {selectedPatient.firstName} {selectedPatient.lastName}
                </div>
                <div className="text-xs text-muted-foreground">
                  Appointment {currentPatientAppointmentIndex + 1} of {selectedPatientAppointments.length}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation(); // Prevent card expansion
                  handleClear();
                }}
                className="p-1 rounded-full hover:bg-destructive/10 hover:text-destructive transition-colors"
                title="Clear patient filter"
              >
                <XMarkIcon className="w-4 h-4 text-muted-foreground hover:text-destructive" />
              </button>
              <ChevronRightIcon className="w-4 h-4 text-muted-foreground" />
            </div>
          </div>
        </div>
      )}

      <div className="relative">
        {/* Search Results Dropdown */}
        {showDropdown && filteredPatients.length > 0 && (
          <div className="absolute z-50 w-full mt-2 bg-background border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {filteredPatients.map((patient) => (
              <button
                key={patient.id}
                onClick={() => {
                  console.log('🖱️ Patient button clicked:', patient);
                  handlePatientSelect(patient);
                }}
                className="w-full px-4 py-3 text-left hover:bg-muted/50 focus:bg-muted/50 focus:outline-none transition-colors border-b border-border last:border-b-0"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-card-foreground">
                      {patient.firstName} {patient.lastName}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {patient.email && (
                        <span className="mr-4">{patient.email}</span>
                      )}
                      {patient.phone && (
                        <span>{patient.phone}</span>
                      )}
                    </div>
                  </div>
                  <CalendarIcon className="h-5 w-5 text-muted-foreground" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Expanded Patient Card - Enhanced Navigation */}
      {selectedPatient && patientAppointments.length > 0 && isCardExpanded && (
        <div className="absolute z-40 w-full mt-2 bg-card border border-border rounded-lg shadow-lg p-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <UserIcon className="h-4 w-4 text-primary" />
              <span className="font-medium text-sm">
                {selectedPatient.firstName} {selectedPatient.lastName}
              </span>
              <span className="px-2 py-0.5 text-xs bg-primary/10 text-primary rounded-full border border-primary/20">
                {currentAppointmentIndex + 1}/{patientAppointments.length}
              </span>
            </div>
            <div className="flex items-center gap-1">
              {/* Navigation Controls */}
              {patientAppointments.length > 1 && (
                <>
                  <button
                    onClick={handleNextAppointment}
                    disabled={currentAppointmentIndex === 0}
                    className="p-1.5 text-muted-foreground hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors rounded hover:bg-muted/50"
                    title="Newer appointment"
                  >
                    <ChevronLeftIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={handlePreviousAppointment}
                    disabled={currentAppointmentIndex === patientAppointments.length - 1}
                    className="p-1.5 text-muted-foreground hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors rounded hover:bg-muted/50"
                    title="Older appointment"
                  >
                    <ChevronRightIcon className="h-4 w-4" />
                  </button>
                </>
              )}
              {/* Collapse Button */}
              <button
                onClick={() => setIsCardExpanded(false)}
                className="p-1.5 text-muted-foreground hover:text-card-foreground transition-colors rounded hover:bg-muted/50"
                title="Collapse card"
              >
                <ChevronLeftIcon className="h-4 w-4 rotate-90" />
              </button>
              <button
                onClick={handleClear}
                className="p-1.5 text-muted-foreground hover:text-card-foreground transition-colors rounded hover:bg-muted/50"
                title="Clear selection"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Current Appointment Preview */}
          {currentAppointment && (
            <div className="bg-muted/30 rounded p-3 text-sm">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4 text-primary" />
                  <span className="font-medium">
                    {formatDate(currentAppointment.appointment_date, currentAppointment.appointment_time).date}
                  </span>
                  <span className="text-muted-foreground">
                    at {formatDate(currentAppointment.appointment_date, currentAppointment.appointment_time).time}
                  </span>
                </div>
                <span className={`px-2 py-0.5 text-xs rounded-full border font-medium ${getStatusColor(currentAppointment.status)}`}>
                  {currentAppointment.status}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-3 text-xs text-muted-foreground mb-3">
                <div>
                  <span className="font-medium">Treatment:</span> {currentAppointment.appointment_type || 'General'}
                </div>
                <div>
                  <span className="font-medium">Duration:</span> {currentAppointment.duration} min
                </div>
                <div>
                  <span className="font-medium">Provider:</span> {currentAppointment.dentist_name || 'Not assigned'}
                </div>
                <div>
                  <span className="font-medium">Status:</span> {currentAppointment.status}
                </div>
              </div>

              {currentAppointment.notes && (
                <div className="mb-3">
                  <div className="text-xs font-medium text-muted-foreground mb-1">Notes:</div>
                  <div className="text-xs bg-background/60 rounded px-2 py-1 border">
                    {currentAppointment.notes}
                  </div>
                </div>
              )}
              
              <button
                onClick={() => navigateToAppointmentWithCalendar(currentAppointmentIndex)}
                className="w-full px-3 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 font-medium"
              >
                <EyeIcon className="h-4 w-4" />
                <span>Navigate to Appointment</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* No Appointments Found - Still Selectable */}
      {selectedPatient && patientAppointments.length === 0 && !isLoadingAppointments && !isCardExpanded && (
        <div 
          className="mt-2 bg-primary/5 border border-primary/20 rounded-lg p-3 cursor-pointer hover:bg-primary/10 transition-colors"
          onClick={() => setIsCardExpanded(true)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                <UserIcon className="w-4 h-4 text-primary" />
              </div>
              <div>
                <div className="font-medium text-foreground text-sm">
                  {selectedPatient.firstName} {selectedPatient.lastName}
                </div>
                <div className="text-xs text-muted-foreground">
                  0 appointments
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation(); // Prevent card expansion
                  handleClear();
                }}
                className="p-1 rounded-full hover:bg-destructive/10 hover:text-destructive transition-colors"
                title="Clear patient filter"
              >
                <XMarkIcon className="w-4 h-4 text-muted-foreground hover:text-destructive" />
              </button>
              <ChevronRightIcon className="w-4 h-4 text-muted-foreground" />
            </div>
          </div>
        </div>
      )}

      {/* No Appointments Found - Expanded View */}
      {selectedPatient && patientAppointments.length === 0 && !isLoadingAppointments && isCardExpanded && (
        <div className="absolute z-40 w-full mt-2 bg-card border border-border rounded-lg shadow-lg p-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <UserIcon className="h-4 w-4 text-primary" />
              <span className="font-medium text-sm">
                {selectedPatient.firstName} {selectedPatient.lastName}
              </span>
              <span className="px-2 py-0.5 text-xs bg-primary/10 text-primary rounded-full border border-primary/20">
                0 appointments
              </span>
            </div>
            <div className="flex items-center gap-1">
              {/* Collapse Button */}
              <button
                onClick={() => setIsCardExpanded(false)}
                className="p-1.5 text-muted-foreground hover:text-card-foreground transition-colors rounded hover:bg-muted/50"
                title="Collapse card"
              >
                <ChevronLeftIcon className="h-4 w-4 rotate-90" />
              </button>
              <button
                onClick={handleClear}
                className="p-1.5 text-muted-foreground hover:text-card-foreground transition-colors rounded hover:bg-muted/50"
                title="Clear selection"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* No Appointments Message */}
          <div className="bg-muted/30 rounded p-3 text-sm text-center">
            <CalendarIcon className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-muted-foreground font-medium mb-1">{t('noAppointmentsFound')}</p>
            <p className="text-xs text-muted-foreground">
              {t('noScheduledAppointments')}
            </p>
          </div>
        </div>
      )}

      {/* Loading Appointments */}
      {selectedPatient && isLoadingAppointments && (
        <div className="absolute z-40 w-full mt-2 bg-card border border-border rounded-lg shadow-lg p-3 text-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto mb-2"></div>
          <p className="text-xs text-muted-foreground">Loading appointments...</p>
        </div>
      )}
    </div>
  );
}